import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

/**
 * Reusable inquiry form modal.
 * Props:
 *   isOpen: boolean
 *   onClose: () => void
 *   programName: string  — e.g. "Flex Detox" or "Personal Training"
 *   tierName?: string    — e.g. "Glow Up" (pre-fills "what caught your eye")
 *   source?: string      — for analytics, e.g. "flex-detox-tier-modal"
 *
 * Tip: Parent should add a `key` that changes on each open (e.g. tierName + opened-at)
 *      so the form state is reset between opens.
 */
const InquiryFormModal = ({ isOpen, onClose, programName, tierName, source }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [caughtEye, setCaughtEye] = useState(tierName ? `${tierName} tier` : '');
  const [message, setMessage] = useState('');
  const [infoSession, setInfoSession] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setError('Please share your name and email so Jill can reach you.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API}/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          program: programName || '',
          tier: tierName || '',
          message: `${caughtEye ? `What caught my eye: ${caughtEye}\n\n` : ''}${message}`.trim(),
          info_session_interest: infoSession,
          source: source || '',
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setDone(true);
      } else {
        setError(data.detail || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-forest/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="inquiry-form-overlay"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-cream rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="inquiry-form-modal"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-terracotta/15 via-amber-100 to-emerald-50 p-6 md:p-8 relative">
            <button
              onClick={onClose}
              className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/40 text-forest"
              data-testid="close-inquiry"
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={18} className="text-terracotta" />
              <p className="text-xs font-body text-forest/70 uppercase tracking-[0.2em]">Reach Out</p>
            </div>
            <h2 className="font-heading text-2xl md:text-3xl text-forest">
              {tierName ? `Inquire about ${tierName}` : `Inquire about ${programName || 'Program'}`}
            </h2>
            <p className="font-body text-stone-600 text-sm mt-1">
              Share a few details and Jill will be in touch.
            </p>
          </div>

          {/* Body */}
          <div className="p-6 md:p-8">
            {done ? (
              <div className="text-center py-6" data-testid="inquiry-success">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 mb-4">
                  <Check size={24} className="text-emerald-700" />
                </div>
                <h3 className="font-heading text-2xl text-forest mb-2">Thank you!</h3>
                <p className="font-body text-stone-600 leading-relaxed mb-6">
                  Jill will be in touch within 48 hours with info session details.
                </p>
                <button
                  onClick={onClose}
                  className="bg-forest text-cream font-body font-medium px-6 py-2.5 rounded-full hover:bg-forest-dark transition-colors"
                  data-testid="inquiry-done"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="inquiry-form">
                <div>
                  <label className="block text-xs font-body text-stone-600 uppercase tracking-wider mb-1.5">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    data-testid="inquiry-name"
                    className="w-full px-4 py-2.5 rounded-xl border border-stone-300 bg-white font-body text-forest placeholder-stone-400 focus:outline-none focus:border-forest"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label className="block text-xs font-body text-stone-600 uppercase tracking-wider mb-1.5">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="inquiry-email"
                    className="w-full px-4 py-2.5 rounded-xl border border-stone-300 bg-white font-body text-forest placeholder-stone-400 focus:outline-none focus:border-forest"
                    placeholder="you@example.com"
                  />
                </div>

                <div>
                  <label className="block text-xs font-body text-stone-600 uppercase tracking-wider mb-1.5">
                    Phone <span className="text-stone-400 normal-case font-normal">(optional)</span>
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    data-testid="inquiry-phone"
                    className="w-full px-4 py-2.5 rounded-xl border border-stone-300 bg-white font-body text-forest placeholder-stone-400 focus:outline-none focus:border-forest"
                    placeholder="(optional)"
                  />
                </div>

                <div>
                  <label className="block text-xs font-body text-stone-600 uppercase tracking-wider mb-1.5">
                    What caught your eye?
                  </label>
                  <input
                    type="text"
                    value={caughtEye}
                    onChange={(e) => setCaughtEye(e.target.value)}
                    data-testid="inquiry-caught-eye"
                    className="w-full px-4 py-2.5 rounded-xl border border-stone-300 bg-white font-body text-forest placeholder-stone-400 focus:outline-none focus:border-forest"
                    placeholder="e.g. Glow Up tier, the chakra-aligned recipes…"
                  />
                </div>

                <div>
                  <label className="block text-xs font-body text-stone-600 uppercase tracking-wider mb-1.5">
                    Anything else you&apos;d like Jill to know? <span className="text-stone-400 normal-case font-normal">(optional)</span>
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={3}
                    data-testid="inquiry-message"
                    className="w-full px-4 py-2.5 rounded-xl border border-stone-300 bg-white font-body text-forest placeholder-stone-400 focus:outline-none focus:border-forest resize-none"
                    placeholder="Goals, timing, questions…"
                  />
                </div>

                <label className="flex items-start gap-3 cursor-pointer bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-testid="inquiry-info-session-label">
                  <input
                    type="checkbox"
                    checked={infoSession}
                    onChange={(e) => setInfoSession(e.target.checked)}
                    data-testid="inquiry-info-session"
                    className="mt-1 w-4 h-4 accent-forest"
                  />
                  <span className="font-body text-sm text-stone-700">
                    I&apos;d like to attend the next info session.
                  </span>
                </label>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm font-body text-red-700" data-testid="inquiry-error">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="inquiry-submit"
                  className="w-full bg-forest text-cream font-body font-medium py-3 rounded-full hover:bg-forest-dark transition-colors disabled:opacity-60"
                >
                  {submitting ? 'Sending…' : 'Send Inquiry'}
                </button>

                <p className="text-xs font-body text-stone-500 text-center">
                  Your info is safe with us — sent directly to Jill, never shared.
                </p>
              </form>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InquiryFormModal;
